/**
 * Active Searches View.
 *
 */
import React, { useEffect, useState, useMemo } from "react";

import moment from "moment";
import sortBy from "lodash/sortBy";
import { useSelector, useDispatch } from "react-redux";

import { Cached as CachedIcon } from "@mui/icons-material";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme
} from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import makeExpandedSearchesTableData from "@/helpers/makeExpandedSearchesTableData";
import { rangeSelectionMap } from "@/helpers/dateRanges";

import ActiveSearchesTableExpanded from "@/components/tables/ActiveSearchesTableExpanded";
import SubmissionsBarDrawer from "@/components/SubmissionsBarDrawer";
import { constants as styleConstants } from "@/styles/config";

import { useFetchAllSearches } from "@/hooks";
import "react-datepicker/dist/react-datepicker.css";

const useStyles = makeStyles(() => ({
  container: {
    padding: styleConstants.desktopDrawerClosedWidth / 2,
    maxWidth: "100vw",
    minHeight: "100vh",
    backgroundColor: "white"
  },
  content: {
    paddingLeft: styleConstants.desktopDrawerClosedWidth
  },
  pageTitle: {
    display: "flex",
    justifyContent: "space-between",
    width: "calc(100% - 64px)",
    marginLeft: styleConstants.desktopDrawerClosedWidth
  },
  titleText: { fontSize: 24, fontWeight: "bold" }
}));

export default function ActiveSearchesView() {
  const theme = useTheme();
  const classes = useStyles();

  const [submissionsBarDrawer, setSubmissionsBarDrawer] = useState({
    open: false,
    type: null,
    data: null
  });

  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.user);

  const { allSearches, loading, fetchAllSearches } = useFetchAllSearches();

  const { rangeSelection, customDateRange } = useSelector((state) => state.app);

  const currentOwner = useMemo(() => {
    return {
      id: user.id,
      name: `${user.firstName} ${user.lastName}`
    };
  }, [user]);

  const expandedSearchesTableData = useMemo(() => {
    if (currentOwner && allSearches) {
      return makeExpandedSearchesTableData({
        searches: allSearches,
        currentOwner
      });
    }
  }, [allSearches, currentOwner]);

  useEffect(() => {
    dispatch.jobs.setCurrentJob(null);
  }, [dispatch]);

  const handleDateRangeSelectionChange = (newValue, customRange) => {
    if (newValue !== rangeSelection) dispatch.app.setRangeSelection(newValue);

    if (newValue === "lifetime") {
      const searchesSortedByDate = allSearches && sortBy(allSearches, "dateAdded");

      const newDateRange = {
        ...rangeSelectionMap[rangeSelection],
        startDate: moment(searchesSortedByDate[0].dateAdded),
        endDate: moment()
      };

      dispatch.app.setDateRange(newDateRange);
      // setPickerDates(newDateRange);
    } else if (newValue === "custom") {
      dispatch.app.setDateRange(customRange);
      // setPickerDates(customRange);
    } else {
      const newDateRange = rangeSelectionMap[newValue];

      dispatch.app.setDateRange(newDateRange);
      // setPickerDates(newDateRange);
    }
  };

  // ↓ renders ↓
  return (
    <Container className={classes.container} maxWidth={false}>
      <Box className={classes.pageTitle}>
        <div>
          <Typography className={classes.titleText}>Job Search Progress</Typography>

          <Typography variant="subtitle2">
            Showing submissions for {rangeSelectionMap[rangeSelection]?.title}
          </Typography>
        </div>

        <Tooltip title="Reload">
          <span>
            <IconButton
              disabled={loading}
              color="inherit"
              size="small"
              onClick={() => fetchAllSearches({ skipCache: true })}
              className="icon-button__backgrounded"
              style={{
                borderRadius: 5,
                height: 36,
                width: 36
              }}
            >
              {loading && <CircularProgress size={16} />}
              {!loading && <CachedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box className={classes.content}>
        <ActiveSearchesTableExpanded
          style={{ marginTop: theme.spacing(2) }}
          loading={loading}
          handleDateRangeSelectionChange={handleDateRangeSelectionChange}
          rangeSelectionMap={rangeSelectionMap}
          rangeSelection={rangeSelection}
          setRangeSelection={(payload) => dispatch.app.setRangeSelection(payload)}
          customDateRange={customDateRange}
          setCustomDateRange={(payload) => dispatch.app.setCustomDateRange(payload)}
          currentOwner={{
            id: user.id,
            name: `${user.firstName} ${user.lastName}`
          }}
          skipPageReset={false}
          handleSubmissionsBarClick={({ data, submissions, job }) => {
            setSubmissionsBarDrawer({
              open: true,
              type: job?.title,
              data: { submissions, initialStatusGroup: data.id }
            });
          }}
          data={expandedSearchesTableData || []}
        />
      </Box>

      <SubmissionsBarDrawer
        componentTitle="Active Searches"
        open={submissionsBarDrawer}
        toggleDrawer={setSubmissionsBarDrawer}
      />
    </Container>
  );
}
